import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Zap, Plus, CheckCircle2, Clock, ArrowRight, Loader2, Trash2, User, Building2, Calendar, Sparkles
} from 'lucide-react';
import useJevSuggest from '../hooks/useJevSuggest';
import { toast } from 'sonner';
import UserAvatar from '../components/UserAvatar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const STATUS_STYLES = {
  pending: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Pending' },
  in_progress: { color: 'bg-amber-100 text-amber-700', icon: ArrowRight, label: 'In Progress' },
  completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Done' },
};

export default function QuickTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState('active');
  const [form, setForm] = useState({ title: '', description: '', assigned_to_id: '', store_id: '', priority: 'medium', due_date: '' });
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({ priority: false, due_date: false });
  const hintEnabled = createOpen && form.title.trim().length >= 4;
  const { suggestion: hint, loading: hinting } = useJevSuggest('quick-task', { title: form.title, description: form.description }, hintEnabled);

  // Smart defaults: priority + "due today" until the user sets their own.
  useEffect(() => {
    if (!hint) return;
    const today = new Date().toISOString().slice(0, 10);
    setForm(f => ({
      ...f,
      priority: (!touched.priority && hint.apply_priority) ? hint.priority.value : f.priority,
      due_date: (!touched.due_date && !f.due_date && hint.due_today >= 0.7) ? today : f.due_date,
    }));
  }, [hint, touched.priority, touched.due_date]);

  useEffect(() => { fetchTasks(); fetchUsers(); fetchStores(); }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/quick-tasks`);
      setTasks(res.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/users`);
      setUsers(res.data.filter(u => u.approved));
    } catch (err) {
      console.warn('Failed to fetch users', err);
    }
  };

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API_URL}/stores`);
      setStores(res.data);
    } catch (err) {
      console.warn('Failed to fetch stores', err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.assigned_to_id) { toast.error('Fill in title and assignee'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/quick-tasks`, form);
      toast.success('Task assigned! Notification sent.');
      setCreateOpen(false);
      setForm({ title: '', description: '', assigned_to_id: '', store_id: '', priority: 'medium', due_date: '' });
      setTouched({ priority: false, due_date: false });
      fetchTasks();
    } catch { toast.error('Failed to create task'); }
    finally { setSubmitting(false); }
  };

  const updateStatus = async (taskId, status) => {
    try {
      await axios.put(`${API_URL}/quick-tasks/${taskId}`, { status });
      fetchTasks();
      toast.success(status === 'completed' ? 'Task completed!' : 'Status updated');
    } catch { toast.error('Failed to update'); }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/quick-tasks/${taskId}`);
      fetchTasks();
      toast.success('Task deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = tasks.filter(t => {
    if (filter === 'active') return t.status !== 'completed';
    if (filter === 'completed') return t.status === 'completed';
    return true;
  });

  const stats = {
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
  };

  const canCreate = ['owner', 'area_manager', 'manager', 'supervisor'].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="quick-tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Quick Tasks</h1>
          <p className="text-sm text-slate-500 mt-0.5">Assign and track instant tasks</p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} className="bg-violet-500 hover:bg-violet-600 text-white rounded-xl h-9 px-4 text-[13px] font-medium btn-press" data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Assign Task
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.pending}</p><p className="text-xs text-slate-500">Pending</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{stats.in_progress}</p><p className="text-xs text-slate-500">In Progress</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p><p className="text-xs text-slate-500">Completed</p>
        </CardContent></Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {['active', 'completed', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${filter === f ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`filter-${f}`}>
            {f === 'active' ? 'Active' : f === 'completed' ? 'Completed' : 'All'}
          </button>
        ))}
      </div>

      {/* Task List */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="py-12 text-center">
          <Zap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">No tasks found</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(task => {
            const st = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
            const Icon = st.icon;
            const isAssignee = task.assigned_to_id === user?.id;
            return (
              <Card key={task.id} className="border-slate-200 hover:shadow-sm transition-all" data-testid={`task-${task.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">{task.title}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}><Icon className="w-3 h-3" />{st.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_STYLES[task.priority]}`}>{task.priority}</span>
                      </div>
                      {task.description && <p className="text-sm text-slate-500 mb-2">{task.description}</p>}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                        <span className="flex items-center gap-1.5" data-testid={`task-assignee-${task.id}`}><UserAvatar name={task.assigned_to_name} picture={task.assigned_to_picture} size="xs" className="!w-5 !h-5" /> {task.assigned_to_name}</span>
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {task.store_name}</span>
                        {task.due_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Due: {task.due_date}</span>}
                        <span>by {task.created_by_name}</span>
                        {task.status === 'completed' && task.completed_by_name && (
                          <span className="flex items-center gap-1.5 text-green-600" data-testid={`task-completed-by-${task.id}`}>
                            <UserAvatar name={task.completed_by_name} picture={task.completed_by_picture} size="xs" className="!w-5 !h-5" />
                            Completed by {task.completed_by_name}{task.completed_at ? ` · ${new Date(task.completed_at).toLocaleDateString()}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {task.status === 'pending' && isAssignee && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(task.id, 'in_progress')} className="text-amber-600 border-amber-200 hover:bg-amber-50">
                          <ArrowRight className="w-3 h-3 mr-1" /> Start
                        </Button>
                      )}
                      {task.status === 'in_progress' && isAssignee && (
                        <Button size="sm" onClick={() => updateStatus(task.id, 'completed')} className="bg-green-500 hover:bg-green-600 text-white">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                        </Button>
                      )}
                      {canCreate && (
                        <Button size="sm" variant="ghost" onClick={() => deleteTask(task.id)} className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Assign Quick Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Task Title</Label>
              <Input placeholder="e.g., Clean fryer filters" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="task-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input placeholder="Additional details..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={form.assigned_to_id} onValueChange={v => setForm({ ...form, assigned_to_id: v })}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => { setTouched(t => ({ ...t, priority: true })); setForm({ ...form, priority: v }); }}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date (optional)</Label>
                <Input type="date" value={form.due_date} onChange={e => { setTouched(t => ({ ...t, due_date: true })); setForm({ ...form, due_date: e.target.value }); }} data-testid="task-due-date-input" />
              </div>
            </div>
            {(hinting || hint) && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs bg-slate-50 rounded-lg px-3 py-2" data-testid="task-smart-defaults">
                <Sparkles className={`w-3.5 h-3.5 text-orange-500 ${hinting ? 'animate-pulse' : ''}`} />
                {hinting && !hint ? <span className="text-slate-400">Rooty is sizing this up…</span> : (
                  <>
                    <span className="font-medium text-slate-700">Rooty suggests:</span>
                    <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200 capitalize" data-testid="task-type-chip">{hint.task_type.value}</span>
                    <span className={`px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[hint.priority.value]}`}>{hint.priority.value}</span>
                    {hint.due_today >= 0.7 && <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Due today</span>}
                  </>
                )}
              </div>
            )}
            {stores.length > 0 && (
              <div className="space-y-2">
                <Label>Store (optional)</Label>
                <Select value={form.store_id} onValueChange={v => setForm({ ...form, store_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select store" /></SelectTrigger>
                  <SelectContent>
                    {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold" data-testid="submit-task-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Zap className="w-4 h-4 mr-2" /> Assign Task</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
